import React from "react";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Button, IconButton, CircularProgress, Box } from "@mui/material";
import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowUpward, Edit } from "@mui/icons-material";
import { EditIcon } from "lucide-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { profileModel, reportsModel } from "../Axios/AxiosConfig";
import ReportStore from "../DataRepository/Reports";
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useMemo } from "react";

import {
  Table,
  TableBody,
  Autocomplete,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Stack
} from "@mui/material";

import { toast, Zoom } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import usePrescriptionStore from "../DataRepository/Prescription";
import LoadingComponent from "../loading"
import DoshaBarChart from "./VikritiChart";

import { CircularProgressbar, buildStyles } from "react-circular-progressbar";

import axios from "axios";
import { teleConsultationModel, shopModel } from "../Axios/AxiosConfig";
import VikritiStore from "../DataRepository/VikiritiStore";
import { Circle, AlertTriangle } from 'lucide-react';

const getStatusTag = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'low':
      return <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 border border-yellow-300">Low</span>;
    case 'high':
      return <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-800 border border-red-300">High</span>;
    case 'normal':
      return <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800 border border-green-300">Normal</span>;
    case 'borderline':
      return <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800 border border-orange-300">Borderline</span>;
    case 'borderline_low':
      return <span className="px-2 py-0.5 text-xs rounded bg-yellow-200 text-yellow-900 border border-yellow-400">Borderline Low</span>;
    case 'borderline_high':
      return <span className="px-2 py-0.5 text-xs rounded bg-orange-200 text-orange-900 border border-orange-400">Borderline High</span>;
    case 'critical':
      return <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 border border-purple-300">Critical</span>;
    case 'missed':
      return <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600 border border-gray-300">Missed</span>;
    default:
      return <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 border border-gray-200">Unknown</span>;
  }
};


const getIcon = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'low':
    case 'high':
      return <AlertTriangle className="text-red-500 w-5 h-5 mr-2" />;
    case 'normal':
      return <CheckCircle className="text-green-500 w-5 h-5 mr-2" />;
  }
};

const headerCellStyle = {
  backgroundColor: '#fff8f0',
  fontWeight: 600,
  fontSize: 14,
  borderBottom: '2px solid #DA6E0B',
  fontFamily: "'Poppins', sans-serif",
  color: '#DA6E0B',
  py: 2,
  textTransform: 'none',
  letterSpacing: '0.02em'
};

const getTrend = (before, after) => {
  if (before == null || after == null) return '→';
  const b = Number(before);
  const a = Number(after);
  if (isNaN(b) || isNaN(a)) return '→';
  if (a > b) return '↑';
  if (a < b) return '↓';
  return '→';
};

// Correct units for common blood / lab parameters that AI often gets wrong
const UNIT_CORRECTIONS = {
  // CBC
  'mcv': 'fL',
  'mean corpuscular volume': 'fL',
  'mch': 'pg',
  'mean corpuscular hemoglobin': 'pg',
  'mchc': 'g/dL',
  'mean corpuscular hemoglobin concentration': 'g/dL',
  'hemoglobin': 'g/dL',
  'haemoglobin': 'g/dL',
  'hgb': 'g/dL',
  'hb': 'g/dL',
  'rbc': '×10⁶/μL',
  'red blood cells': '×10⁶/μL',
  'red blood cell count': '×10⁶/μL',
  'wbc': '×10³/μL',
  'white blood cells': '×10³/μL',
  'white blood cell count': '×10³/μL',
  'total leukocyte count': '×10³/μL',
  'tlc': '×10³/μL',
  'platelets': '×10³/μL',
  'platelet count': '×10³/μL',
  'plt': '×10³/μL',
  'hematocrit': '%',
  'haematocrit': '%',
  'pcv': '%',
  'packed cell volume': '%',
  'hct': '%',
  // Differential
  'neutrophils': '%',
  'lymphocytes': '%',
  'monocytes': '%',
  'eosinophils': '%',
  'basophils': '%',
  // Lipid profile
  'total cholesterol': 'mg/dL',
  'cholesterol': 'mg/dL',
  'ldl': 'mg/dL',
  'ldl cholesterol': 'mg/dL',
  'hdl': 'mg/dL',
  'hdl cholesterol': 'mg/dL',
  'triglycerides': 'mg/dL',
  'vldl': 'mg/dL',
  // Blood glucose
  'fasting blood glucose': 'mg/dL',
  'fasting blood sugar': 'mg/dL',
  'blood glucose': 'mg/dL',
  'blood sugar': 'mg/dL',
  'hba1c': '%',
  'glycated hemoglobin': '%',
  // LFT
  'bilirubin': 'mg/dL',
  'total bilirubin': 'mg/dL',
  'direct bilirubin': 'mg/dL',
  'indirect bilirubin': 'mg/dL',
  'sgot': 'U/L',
  'ast': 'U/L',
  'sgpt': 'U/L',
  'alt': 'U/L',
  'alkaline phosphatase': 'U/L',
  'alp': 'U/L',
  'albumin': 'g/dL',
  'total protein': 'g/dL',
  // KFT
  'creatinine': 'mg/dL',
  'urea': 'mg/dL',
  'blood urea nitrogen': 'mg/dL',
  'bun': 'mg/dL',
  'uric acid': 'mg/dL',
  // Thyroid
  'tsh': 'mIU/L',
  'thyroid stimulating hormone': 'mIU/L',
  't3': 'ng/dL',
  't4': 'μg/dL',
  'free t3': 'pg/mL',
  'free t4': 'ng/dL',
};

const correctUnit = (paramName = '', aiUnit = '') => {
  const key = paramName.trim().toLowerCase();
  return UNIT_CORRECTIONS[key] ?? aiUnit ?? '';
};



function Patient_Profile() {

  let { id } = useParams();

  let { selectedPatientdetails } = usePrescriptionStore();

  const [inputValue, setInputValue] = useState("");

  const [loading, setLoading] = useState(false);

  const { getAIReports, AIReports, isAIReportsLoading, getReports, Reports } = ReportStore();

  const [showAutocomplete, setShowAutocomplete] = useState(false);

  let navigate = useNavigate();

  const [PrakritiCollection, setPrakritiCollection] = useState([]);

  const { Vikriti_data, isVikriti_dataloading, getVikritidata } =
    VikritiStore();

  const filteredVikritiData = Vikriti_data?.filter(
    (item) => item?.sub_category?.toLowerCase() !== "na"
  );

  const [open, setOpen] = useState(false);

  const [vScore, setVScore] = useState(0);

  const [pScore, setPScore] = useState(0);

  const [kScore, setKScore] = useState(0);

  const [VPK, setVPK] = useState("");

  const [diagnosisData, setDiagnosisData] = useState(null);


  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const [open3, setOpen3] = useState(false);

  const handleClose3 = () => {
    setOpen3(false);
  };

  const handleOpen3 = () => {
    setOpen3(true);
  };

  const [isFirstClick, setIsFirstClick] = useState(true);

  // Comparison State - Arrays to support multiple reports
  const [selectedBeforeReports, setSelectedBeforeReports] = useState([]);
  const [selectedAfterReports, setSelectedAfterReports] = useState([]);
  const [openReportDialog, setOpenReportDialog] = useState(false);

  // Helper to check if a report is in either list
  const getReportSelection = (reportId) => {
    if (selectedBeforeReports.includes(reportId)) return 'before';
    if (selectedAfterReports.includes(reportId)) return 'after';
    return null;
  };


  const [open4, setOpen4] = useState(false);

  const [open5, setOpen5] = useState(false);

  const handleClose4 = () => {
    setOpen4(false);
  };

  const handleOpen4 = () => {
    setOpen4(true);
  };

  const handleClose5 = () => {
    setOpen5(false);
  };

  const handleOpen5 = () => {
    setOpen5(true);
  };

  let { setSelectedPatientdetails, setSelectedAppointemntdetails, resetPrescriptionState
  } =
    usePrescriptionStore();

  let [user, setuser] = useState();

  let [vikritidata, setvikritidata] = useState();

  const [searchTerm, setSearchTerm] = useState("");
  const [isVikritiAILoading, setIsVikritiAILoading] = useState(false);
  const [vikritiAIReason, setVikritiAIReason] = useState(null);
  // In-render guard to prevent double-fire within same render cycle
  const vikritiAIFetchedRef = React.useRef(false);


  const cleanJson = (str) => {
    try {
      if (!str) return null;
      if (typeof str === 'object') return str;
      if (typeof str === 'string' && str.includes('AI analysis failed')) return null;
      return JSON.parse(str);
    } catch {
      return null;
    }
  };

  const PARAM_SYNONYMS = {
    haemoglobin: 'hemoglobin',
    hgb: 'hemoglobin',
    pcv: 'pcv',
    hct: 'pcv',
  };

  const normalizeName = (name = '') => {
    const key = name.trim().toLowerCase();
    return PARAM_SYNONYMS[key] || key;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const symptomValue = event.target.value.trim();
      const existingSymptom = filteredVikritiData?.find(
        (item) => item.symptoms.toLowerCase() === symptomValue.toLowerCase()
      );

      if (
        symptomValue &&
        !user?.Vikriti_details?.vikritiList?.some(
          (item) => item.symptoms === symptomValue
        )
      ) {
        let newSymptomObj;

        if (existingSymptom) {
          newSymptomObj = {
            ...existingSymptom,
            sub_category: existingSymptom.sub_category || "na",
            category: existingSymptom.category || "na",
            dhatu_increase: existingSymptom.dhatu_increase || "na",
            dhatu_decrease: existingSymptom.dhatu_decrease || "na",
            dosha_increase: existingSymptom.dosha_increase || "na",
            dosha_decrease: existingSymptom.dosha_decrease || "na",
            mala_increase: existingSymptom.mala_increase || "na",
            mala_decrease: existingSymptom.mala_decrease || "na",
          };

          setuser((prevState) => ({
            ...prevState,
            Vikriti_details: {
              ...prevState?.Vikriti_details,
              vikritiList: [
                ...(prevState?.Vikriti_details?.vikritiList || []),
                newSymptomObj,
              ],
            },
          }));
        } else {
          newSymptomObj = {
            dosha_decrease: "na",
            symptoms: symptomValue,
            sub_category: "na",
            category: "na",
            dhatu_increase: "na",
            dosha_increase: "na",
            id: Math.random().toString(36).substr(2, 9),
            mala_increase: "na",
            mala_decrease: "na",
            dhatu_decrease: "na",
          };

          setuser((prevState) => ({
            ...prevState,
            Vikriti_details: {
              ...prevState?.Vikriti_details,
              vikritiList: [
                ...prevState?.Vikriti_details?.vikritiList,
                newSymptomObj,
              ],
            },
          }));
        }

        setInputValue("");
      }
    }
  };

  // const handleAutocompleteChange = (event, value) => {
  //     if (value != null) {
  //         const symptomValue = value?.trim();
  //         const existingSymptom = filteredVikritiData?.find(item => item.symptoms.toLowerCase() === symptomValue.toLowerCase());

  //         if (symptomValue && !user?.Vikriti_details?.vikritiList?.some((item) => item.symptoms === symptomValue)) {
  //             let newSymptomObj;

  //             if (existingSymptom) {
  //                 newSymptomObj = {
  //                     ...existingSymptom,
  //                     sub_category: existingSymptom.sub_category || 'na',
  //                     category: existingSymptom.category || 'na',
  //                     dhatu_increase: existingSymptom.dhatu_increase || 'na',
  //                     dhatu_decrease: existingSymptom.dhatu_decrease || 'na',
  //                     dosha_increase: existingSymptom.dosha_increase || 'na',
  //                     dosha_decrease: existingSymptom.dosha_decrease || 'na',
  //                     mala_increase: existingSymptom.mala_increase || 'na',
  //                     mala_decrease: existingSymptom.mala_decrease || 'na'
  //                 };
  //                 setuser((prevState) => ({
  //                     ...prevState,
  //                     Vikriti_details: {
  //                         ...prevState?.Vikriti_details,
  //                         vikritiList: [...prevState?.Vikriti_details?.vikritiList, newSymptomObj]
  //                     }
  //                 }));
  //             } else {
  //                 newSymptomObj = {
  //                     dosha_decrease: "na",
  //                     symptoms: symptomValue,
  //                     sub_category: "na",
  //                     category: "na",
  //                     dhatu_increase: "na",
  //                     dosha_increase: "na",
  //                     id: Math.random().toString(36).substr(2, 9),
  //                     mala_increase: "na",
  //                     mala_decrease: "na",
  //                     dhatu_decrease: "na"
  //                 };

  //                 setuser((prevState) => ({
  //                     ...prevState,
  //                     Vikriti_details: {
  //                         ...prevState?.Vikriti_details,
  //                         vikritiList: [...prevState?.Vikriti_details?.vikritiList, newSymptomObj]
  //                     }
  //                 }));
  //             }

  //             setInputValue('');
  //         }
  //     }
  // };

  function findVPKResult(arrayList) {
    try {
      const [vRaw, pRaw, kRaw] = arrayList;

      // Check for valid numeric input
      const isValid = (val) => typeof val === "number" && !isNaN(val);

      if (![vRaw, pRaw, kRaw].every(isValid)) return "";

      const datamap = {
        V: vRaw,
        P: pRaw,
        K: kRaw,
      };

      const datamap1 = {
        [vRaw]: "V",
        [pRaw]: "P",
        [kRaw]: "K",
      };

      const sorted = [vRaw, pRaw, kRaw].sort((a, b) => b - a); // descending
      const v = sorted[0];
      const k = sorted[2];
      const p = vRaw + pRaw + kRaw - v - k;

      if (v - p > 20) {
        return datamap1[v];
      } else if (v - p <= 20 && p - k > 5) {
        if (v === p) {
          const dominant = Object.entries(datamap)
            .filter(([key, value]) => value === v)
            .map(([key]) => key)
            .join("");
          return dominant;
        } else {
          return datamap1[v] + datamap1[p];
        }
      } else {
        return datamap1[v] + datamap1[p] + datamap1[k];
      }
    } catch (error) {
      console.error("Error in findVPKResult:", error);
      return "";
    }
  };

  const getPatientProfile = async () => {
    try {
      setLoading(true);
      let res = await teleConsultationModel.post("getPatientProfile", {
        id,
      });
      if (res.status == 200) {
        setuser(res.data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getVikritiData = async () => {
    try {
      let res = await profileModel.get("fetchVikritidata", {
        id,
      });
      if (res.status == 200) {
        setvikritidata(res.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const symptomsList = vikritidata
    ?.map((data) => data.symptoms)
    .filter((symptom) => symptom);

  useEffect(() => {
    getPatientProfile();
    getVikritiData();

  }, []);

  useEffect(() => {
    getReports(user?.Patient_details?.UID, id)
  }, [user])

  // Auto-fetch AI Vikriti if Result or reason is empty/None
  const fetchVikritiFromAI = async (uid) => {
    try {
      setIsVikritiAILoading(true);

      const payload = {
        UID: uid,
        patient_details: user?.Patient_details,
        chief_complaints: user?.Vikriti_details?.vikritiList || [],
        ayurvedic_assessment: {},
        vital_signs: {},
        prakriti_dosha: user?.Prakriti_details?.result || {},
        past_history: [],
        current_drugs: [],
        ai_reports: [],
        vikriti_dosha: "",
      };

      const res = await profileModel.post("/getVikritiFromAIWithParam", payload);

      if (res.status === 200) {
        let cleanData = res?.data?.analysis;
        const parsedData = typeof cleanData === "string" ? JSON.parse(cleanData) : cleanData;

        // Derive Result from increased_dosha
        const increasedDosha = (parsedData?.increased_dosha?.dosha || "").toLowerCase();
        const decreasedDosha = (parsedData?.decreased_dosha?.dosha || "").toLowerCase();
        const resultMap = { vata: "V", pitta: "P", kapha: "K" };
        const resultCode = resultMap[increasedDosha] || "";
        const result = resultCode || "None";

        // Derive scores from AI analysis:
        // increased = +60 (positive, tall bar)
        // neutral   = +20 (positive, short bar)
        // decreased = -40 (NEGATIVE, inverted bar below X-axis)
        const allDoshas = ["vata", "pitta", "kapha"];
        const neutralDosha = allDoshas.find(d => d !== increasedDosha && d !== decreasedDosha) || "";
        const scoreMap = { increased: 60, neutral: 20, decreased: -40 };
        const roleMap = {
          [increasedDosha]: "increased",
          [neutralDosha]: "neutral",
          [decreasedDosha]: "decreased",
        };
        const doshaScores = {
          vata: scoreMap[roleMap["vata"]] ?? 0,
          pitta: scoreMap[roleMap["pitta"]] ?? 0,
          kapha: scoreMap[roleMap["kapha"]] ?? 0,
        };

        const generatedReason = [
          parsedData?.increased_dosha?.reason ? `Increased (${increasedDosha}): ${parsedData.increased_dosha.reason}` : "",
          parsedData?.decreased_dosha?.reason ? `Decreased (${decreasedDosha}): ${parsedData.decreased_dosha.reason}` : "",
        ].filter(Boolean).join(" ") || "Reason generated";

        // Mark as fetched in sessionStorage — persists across component remounts
        // within the same browser tab session
        sessionStorage.setItem(`vikriti_ai_done_${uid}`, "1");

        // Update local state
        setuser((prevUser) => ({
          ...prevUser,
          Vikriti_details: {
            ...prevUser?.Vikriti_details,
            Result: result,
            reason: generatedReason,
            vata: doshaScores.vata,
            pitta: doshaScores.pitta,
            kapha: doshaScores.kapha,
          },
        }));

        setVikritiAIReason(generatedReason);

        // Persist to backend (fire and forget)
        profileModel.post("/UpdateVikriti", {
          UID: uid,
          count: user?.Vikriti_details?.vikritiList?.length || 0,
          date: Date?.now()?.toString(),
          vataPulseScore: 0,
          pittaPulseScore: 0,
          kaphaPulseScore: 0,
          Result: result,
          reason: generatedReason,
          vata: doshaScores.vata,
          pitta: doshaScores.pitta,
          kapha: doshaScores.kapha,
          vikritiList: user?.Vikriti_details?.vikritiList,
        }).catch(err => console.error("Failed to persist Vikriti:", err));
      }
    } catch (err) {
      console.error("Error fetching AI Vikriti:", err);
    } finally {
      setIsVikritiAILoading(false);
    }
  };

  useEffect(() => {
    const result = user?.Vikriti_details?.Result;
    const reason = user?.Vikriti_details?.reason;
    const uid = user?.Patient_details?.UID || user?.Appointment_details?.patient_id;

    if (!uid || isVikritiAILoading) return;

    // In-render dedup: prevent double-fire from rapid re-renders
    if (vikritiAIFetchedRef.current) return;

    // Check if DB already has real computed data
    const hasRealData =
      user?.Vikriti_details?.vata != null &&
      result && result !== "None" && result !== "invalid" &&
      reason && reason !== "None";

    // sessionStorage guard only blocks if DB also has real data.
    // If DB is empty/stale despite the key, ignore it and fetch anyway.
    const sessionKey = `vikriti_ai_done_${uid}`;
    if (sessionStorage.getItem(sessionKey) && hasRealData) return;

    // Only fetch if Result or reason is missing/None/invalid
    if (!result || result === "None" || result === "invalid" || !reason || reason === "None") {
      vikritiAIFetchedRef.current = true;
      fetchVikritiFromAI(uid);
    }
  }, [
    user?.Vikriti_details?.Result,
    user?.Vikriti_details?.reason,
    user?.Vikriti_details?.vata,
    user?.Patient_details?.UID,
    user?.Appointment_details?.patient_id,
    user?.Vikriti_details?.vikritiList?.length,
  ]);



  function calculateDoshaValues(vikritiResponse = {}) {
    const { vata, pitta, kapha, Result } = vikritiResponse || {};
    const uniqueCategories = new Set();

    if (
      typeof vata === 'number' &&
      typeof pitta === 'number' &&
      typeof kapha === 'number' &&
      typeof Result === 'string'
    ) {
      return {
        doshaValues: { v: vata, p: pitta, k: kapha },
        uniqueCategories: [],
      };
    }

    if (!vikritiResponse?.vikritiList || vikritiResponse?.vikritiList.length === 0) {
      const v = vikritiResponse?.vataAssScore || vikritiResponse?.vata || 0;
      const p = vikritiResponse?.pittaAssScore || vikritiResponse?.pitta || 0;
      const k = vikritiResponse?.kaphaAssScore || vikritiResponse?.kapha || 0;

      return {
        doshaValues: { v, p, k },
        uniqueCategories: [],
      };
    }

    let v = 0, p = 0, k = 0;

    vikritiResponse.vikritiList.forEach((vikriti) => {
      vikriti?.dosha_increase?.split(',')?.forEach((dosha) => {
        switch (dosha.trim().toLowerCase()) {
          case 'v': v++; break;
          case 'p': p++; break;
          case 'k': k++; break;
        }
      });

      vikriti?.dosha_decrease?.split(',')?.forEach((dosha) => {
        switch (dosha.trim().toLowerCase()) {
          case 'v': v--; break;
          case 'p': p--; break;
          case 'k': k--; break;
        }
      });

      if (vikriti?.category) uniqueCategories.add(vikriti.category);
    });

    return {
      doshaValues: { v, p, k },
      uniqueCategories: Array.from(uniqueCategories),
    };
  }

  function calculatePercentages(vikritiResponse = {}) {

    const { vata, pitta, kapha, Result } = vikritiResponse || {};

    const isNonZeroNumber = n => typeof n === 'number' && Number.isFinite(n) && n !== 0;

    if (
      isNonZeroNumber(vata) &&
      isNonZeroNumber(pitta) &&
      isNonZeroNumber(kapha) &&
      typeof Result === 'string'
    ) {
      return {
        doshaPercentages: { v: vata, p: pitta, k: kapha },
        uniqueCategories: [],
      };
    }

    const { doshaValues, uniqueCategories } = calculateDoshaValues(vikritiResponse);

    const vataFloat = doshaValues?.v || 0;
    const pittaFloat = doshaValues?.p || 0;
    const kaphaFloat = doshaValues?.k || 0;

    const vataPulse = vikritiResponse?.vataPulseScore || 0;
    const pittaPulse = vikritiResponse?.pittaPulseScore || 0;
    const kaphaPulse = vikritiResponse?.kaphaPulseScore || 0;

    const vPercent = 0.7 * vataFloat + 0.3 * vataPulse;
    const pPercent = 0.7 * pittaFloat + 0.3 * pittaPulse;
    const kPercent = 0.7 * kaphaFloat + 0.3 * kaphaPulse;

    return {
      doshaPercentages: {
        v: Math.round(vPercent),
        p: Math.round(pPercent),
        k: Math.round(kPercent),
      },
      uniqueCategories,
    };
  }

  const [editedData, setEditedData] = useState(
    user?.Vikriti_details?.vikritiList || []
  );

  const [openAi, setOpenAi] = useState(false);

  const handleOpenAi = () => {
    setOpenAi(true);
    handleAyurvedaDiagnosisAISuggestion();
  };

  const vikritiresultData = calculatePercentages(user?.Vikriti_details);

  const vikritiscore = {
    vata: user?.Vikriti_details?.vata,
    pitta: user?.Vikriti_details?.pitta,
    kapha: user?.Vikriti_details?.kapha,
  };

  const findDominantDosha = (prakritiState) => {
    if (prakritiState) {
      if (
        prakritiState?.vata >=
        Math.max(prakritiState?.pitta, prakritiState?.kapha)
      ) {
        return ["Vata", "Dominant", `${prakritiState?.vata?.toFixed(0)}%`];
      } else if (
        prakritiState.pitta >=
        Math.max(prakritiState?.vata, prakritiState?.kapha)
      ) {
        return ["Pitta", "Dominant", `${prakritiState?.pitta?.toFixed(0)}%`];
      } else {
        return ["Kapha", "Dominant", `${prakritiState?.kapha?.toFixed(0)}%`];
      }
    }
    return ["", "Dominant", 0];
  };

  const [editQuestionIndex, setEditQuestionIndex] = useState(null);

  const handleEditAnswer = (questionIndex) => {
    setEditQuestionIndex(questionIndex);
  };

  const handleAnswerChange = (questionIndex, newAnswerIndex) => {
    if (!user?.Prakriti_details?.PrakritiCollection) return;

    const updatedPrakritiDetails = user.Prakriti_details.PrakritiCollection.map(
      (q, qIndex) => {
        if (qIndex === questionIndex) {
          return {
            ...q,
            Answers: q.Answers.map((ans, ansIndex) => ({
              ...ans,
              isSelected: ansIndex === newAnswerIndex,
            })),
          };
        }
        return q; // Return other questions as-is
      }
    );

    // Update the user state
    setuser({
      ...user,
      Prakriti_details: {
        ...user.Prakriti_details,
        PrakritiCollection: updatedPrakritiDetails,
      },
    });

    // Exit edit mode
    setEditQuestionIndex(null);
  };

  const handleKeyPress = (event) => {
    const charCode = event?.charCode;
    if (event?.keyCode !== 8 && (charCode < 48 || charCode > 57)) {
      event?.preventDefault();
    }
  };

  const handleSaveChanges = async () => {
    try {
      let localV = 0;
      let localP = 0;
      let localK = 0;

      // Use the collection from state as the source of truth
      const collection = user?.Prakriti_details?.PrakritiCollection || [];

      collection.forEach((question) => {
        const selectedAnswer = question?.Answers?.find(
          (answer) => answer.isSelected
        );
        if (selectedAnswer && selectedAnswer.score && selectedAnswer.vpk) {
          const score = parseFloat(selectedAnswer.score) || 0;
          switch (selectedAnswer.vpk.toLowerCase()) {
            case "v": localV += score; break;
            case "p": localP += score; break;
            case "k": localK += score; break;
          }
        }
      });

      // Update state for UI consistency
      setVScore(localV);
      setPScore(localP);
      setKScore(localK);

      // Trigger submission with the locally calculated values
      await handleSubmitResult(localV, localP, localK);
    } catch (error) {
      console.error("handleSaveChanges: Error occurred", error);
    }
  };

  const handleClick = async () => {
    await handleSaveChanges();
    if (isFirstClick) {
      setIsFirstClick(false);
    }
  };

  const updateScores = (newAnswer) => {
    if (newAnswer && newAnswer?.score && newAnswer?.vpk) {
      const newScore = parseFloat(newAnswer.score) || 0;

      switch (newAnswer.vpk.toLowerCase()) {
        case "v":
          setVScore((prevScore) => prevScore + newScore);
          break;
        case "p":
          setPScore((prevScore) => prevScore + newScore);
          break;
        case "k":
          setKScore((prevScore) => prevScore + newScore);
          break;
      }
    }
  };

  const handleSaveAISuggestion = async (diagnosisData) => {
    try {
      if (!user || !user.Vikriti_details) {
        console.log("User or Vikriti_details not found");
        return;
      }

      let { vataAssScore, pittaAssScore, kaphaAssScore } = user.Vikriti_details;

      if (!diagnosisData || typeof diagnosisData !== "object") {
        console.error("Invalid diagnosisData format");
        return;
      }

      if (diagnosisData.increased_dosha?.dosha) {
        switch (diagnosisData.increased_dosha.dosha.toLowerCase()) {
          case "vata": vataAssScore += 5; break;
          case "pitta": pittaAssScore += 5; break;
          case "kapha": kaphaAssScore += 5; break;
        }
      }

      if (diagnosisData.decreased_dosha?.dosha) {
        switch (diagnosisData.decreased_dosha.dosha.toLowerCase()) {
          case "vata": vataAssScore = Math.max(vataAssScore - 5, 0); break;
          case "pitta": pittaAssScore = Math.max(pittaAssScore - 5, 0); break;
          case "kapha": kaphaAssScore = Math.max(kaphaAssScore - 5, 0); break;
        }
      }

      const total = vataAssScore + pittaAssScore + kaphaAssScore;

      if (total > 0) {
        vataAssScore = (vataAssScore / total) * 100;
        pittaAssScore = (pittaAssScore / total) * 100;
        kaphaAssScore = (kaphaAssScore / total) * 100;
      }

      const doshaOrder = [
        { dosha: "V", score: vataAssScore },
        { dosha: "P", score: pittaAssScore },
        { dosha: "K", score: kaphaAssScore },
      ].sort((a, b) => b.score - a.score)
        .map(d => d.dosha)
        .join("");

      setuser(prevUser => ({
        ...prevUser,
        Vikriti_details: {
          ...prevUser.Vikriti_details,
          vataAssScore: Math.round(vataAssScore),
          pittaAssScore: Math.round(pittaAssScore),
          kaphaAssScore: Math.round(kaphaAssScore),
          Result: doshaOrder,
        }
      }));

      if (diagnosisData) setDiagnosisData(null);


      console.log({
        vataAssScore: Math.round(vataAssScore),
        pittaAssScore: Math.round(pittaAssScore),
        kaphaAssScore: Math.round(kaphaAssScore),
        Result: doshaOrder,
      });

      setOpenAi(false)

    } catch (err) {
      console.error("Error saving AI suggestion:", err);
    }
  };

  function PrakriticalculatePercentages(vScore, pScore, kScore) {

    const totalScore = Math.abs(vScore) + Math.abs(pScore) + Math.abs(kScore);

    if (totalScore > 0) {
      const vScorePercentage = (vScore / totalScore) * 100;
      const pScorePercentage = (pScore / totalScore) * 100;
      const kScorePercentage = (kScore / totalScore) * 100;

      return {
        vScorePercentage: parseFloat(vScorePercentage?.toFixed(2)),
        pScorePercentage: parseFloat(pScorePercentage?.toFixed(2)),
        kScorePercentage: parseFloat(kScorePercentage?.toFixed(2)),
      };
    } else {
      return {
        vScorePercentage: 0.0,
        pScorePercentage: 0.0,
        kScorePercentage: 0.0,
      };
    }
  }

  async function findVPK(arrayList) {
    try {
      const labels = ["V", "P", "K"];
      const indexed = arrayList.map((score, i) => ({ score, label: labels[i] }));

      // Sort by score descending
      const sorted = [...indexed].sort((a, b) => b.score - a.score);
      const v = sorted[0]; // Highest
      const p = sorted[1]; // Middle
      const k = sorted[2]; // Lowest

      if (v.score - p.score > 20) return v.label;

      if (v.score - p.score <= 20 && p.score - k.score > 5) {
        if (v.score === p.score) {
          // If top two are equal, order them by V, P, K priority or alphabetical
          const topTwo = [v, p].sort((a, b) => labels.indexOf(a.label) - labels.indexOf(b.label));
          return topTwo.map(item => item.label).join("");
        }
        return `${v.label}${p.label}`;
      }

      // All three are close
      return "VPK";
    } catch (error) {
      console.error("Error in findVPK:", error);
      throw error;
    }
  }

  const handleSubmitResult = async (cV = vScore, cP = pScore, cK = kScore) => {
    try {
      const percentages = PrakriticalculatePercentages(cV, cP, cK);

      const VPk_result = await findVPK([
        percentages?.vScorePercentage,
        percentages?.pScorePercentage,
        percentages?.kScorePercentage,
      ]);

      setVPK(VPk_result);

      await updatePrakritiResult(
        percentages?.vScorePercentage,
        percentages?.pScorePercentage,
        percentages?.kScorePercentage,
        VPk_result,
        cV,
        cP,
        cK
      );
    } catch (error) {
      console.error("Error in handleSubmitResult:", error);
    }
  };

  const updatePrakritiResult = async (vata, pitta, kapha, VPk_result, rV = vScore, rP = pScore, rK = kScore) => {
    try {
      setLoading(true);
      const res = await profileModel.post("/UpdatePrakriti", {
        UID: user?.Appointment_details?.patient_id,
        result: VPk_result,
        date: Date.now().toString(),
        vata: Math.round(vata),
        pitta: Math.round(pitta),
        kapha: Math.round(kapha),
        rawVata: rV,
        rawKapha: rK,
        rawPitta: rP,
        id: user?.Appointment_details?.patient_id,
        PrakritiCollection: user.Prakriti_details.PrakritiCollection,
      });

      if (res.status === 200) {
        handleClose();
        getPatientProfile();
      }
    } catch (err) {
      console.error(
        "Error updating Prakriti result:",
        err?.response?.data || err
      );
    } finally {
      setLoading(false);
    }
  };

  const EditPrakritiResult = async () => {
    try {
      setLoading(true);

      const resultValue = findVPKResult([
        user?.Prakriti_details?.vata,
        user?.Prakriti_details?.pitta,
        user?.Prakriti_details?.kapha,
      ]);

      const payload = {
        UID: user?.Appointment_details?.patient_id,
        ...user?.Prakriti_details,
        result: resultValue,
      };

      const res = await profileModel.post("/UpdatePrakriti", payload);

      if (res.status === 200) {
        handleClose4();
        getPatientProfile();
      }
    } catch (err) {
      console.error("Error updating Prakriti result:", err.response?.data || err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {

    const newRow = {
      category: "",
      sub_category: "",
      symptoms: "",
      dosha_increase: "",
      dosha_decrease: "",
    };

    setuser((prevUser) => ({
      ...prevUser,

      Vikriti_details: {
        ...prevUser?.Vikriti_details,
        vikritiList: [...(prevUser?.Vikriti_details?.vikritiList || []), newRow],
      },

    }));


  };

  const handleRemove = (index) => {
    setuser((prevUser) => ({
      ...prevUser,
      Vikriti_details: {
        ...prevUser?.Vikriti_details,
        vikritiList: prevUser?.Vikriti_details?.vikritiList.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleEditChange = (index, field, value) => {
    setuser((prevUser) => ({
      ...prevUser,
      Vikriti_details: {
        ...prevUser?.Vikriti_details,
        vikritiList: prevUser?.Vikriti_details?.vikritiList.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  function calculateVikritiDoshaValues(vikritiResponse) {
    let v = 0;
    let p = 0;
    let k = 0;

    const uniqueCategories = new Set();

    vikritiResponse?.forEach(vikriti => {
      vikriti?.dosha_increase?.split(",")?.forEach(dosha => {
        switch (dosha.trim().toLowerCase()) {
          case 'v':
            v++;
            break;
          case 'p':
            p++;
            break;
          case 'k':
            k++;
            break;
        }
      });

      vikriti?.dosha_decrease?.split(",")?.forEach(dosha => {
        switch (dosha.trim().toLowerCase()) {
          case 'v':
            v--;
            break;
          case 'p':
            p--;
            break;
          case 'k':
            k--;
            break;
        }
      });

      uniqueCategories.add(vikriti.category);
    });


    const totalDoshas = vikritiResponse.length;


    const vPercent = (v / totalDoshas) * 100;
    const pPercent = (p / totalDoshas) * 100;
    const kPercent = (k / totalDoshas) * 100;

    return { vataAssScore: v, pittaAssScore: p, kaphaAssScore: k }
  }

  const UpdateVkikriti = async () => {
    try {
      setLoading(true);

      const uid = user?.Appointment_details?.patient_id || user?.Patient_details?.UID;

      let res = await profileModel.post("/UpdateVikriti", {
        UID: uid,
        count: user?.Vikriti_details.vikritiList?.length,
        date: Date?.now()?.toString(),
        vataPulseScore: 0,
        pittaPulseScore: 0,
        kaphaPulseScore: 0,
        vikritiList: user?.Vikriti_details.vikritiList,
        // Clear old AI scores so re-generation triggers
        Result: "None",
        reason: "None",
        vata: null,
        pitta: null,
        kapha: null,
      });

      if (res?.status === 200 && res?.data) {
        handleClose5();
        handleClose3();

        // Clear sessionStorage guard + ref so AI re-fetches with new symptoms
        const uid = user?.Appointment_details?.patient_id || user?.Patient_details?.UID;
        sessionStorage.removeItem(`vikriti_ai_done_${uid}`);
        vikritiAIFetchedRef.current = false;
        setVikritiAIReason(null);
        setuser((prevUser) => ({
          ...prevUser,
          Vikriti_details: {
            ...prevUser?.Vikriti_details,
            Result: "None",
            reason: "None",
            vata: null,
            pitta: null,
            kapha: null,
          },
        }));
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVikritiSavechanges = async () => {
    UpdateVkikriti()
  }

  const handleAyurvedaDiagnosisAISuggestion = async () => {
    try {
      setLoading(true);

      const details = {
        Prakriti: user?.Prakriti_details?.result || "",
        Vikriti: user?.Vikriti_details?.result || "",
        Symptoms:
          Array.isArray(user?.Vikriti_details?.vikritiList) && user?.Vikriti_details?.vikritiList.length
            ? user?.Vikriti_details?.vikritiList.map((e) => e?.symptoms).join(", ")
            : "",
        Gender: selectedPatientdetails?.gender || "",
        Height: selectedPatientdetails?.height || "",
        Weight: selectedPatientdetails?.weight || "",
      };

      const querySuggestion = `
        Given a person whose prakriti is ${details?.Prakriti}, and has symptoms like 
        ${details?.Symptoms}. 
        Please determine the vikriti for this individual mentioning the increased and decreased dosha (vata, pitta, kapha). 
        Provide a concise reasoning in 2 to 3 sentences. Now, return the output in the following JSON format: 
        {
          "increased_dosha": { "dosha": "", "reason": "sample reason." },
          "decreased_dosha": { "dosha": "", "reason": "sample reason." }
        }
      `;

      const res = await reportsModel.post("/checkQuery", {
        query: querySuggestion,
      });

      if (res.status === 200) {
        let cleanData = res.data.trim();
        if (cleanData.startsWith("```json")) {
          cleanData = cleanData.replace(/```json|```/g, "").trim();
        }
        const parsedData = JSON.parse(cleanData);

        setDiagnosisData(parsedData);
        console.log("API Response:", parsedData);

        setLoading(false);
      } else {
        console.log("Unexpected API response:", res);
      }

    } catch (err) {
      console.error("Error fetching AI response:", err);
    } finally {
      setLoading(false);
    }
  };

  const symptom = user?.Vikriti_details?.vikritiList;

  const [diseaseListNew, setDiseaseList] = useState([])

  useEffect(() => {

    const DiseasesLists = async () => {
      try {

        const res = await profileModel.get(`/Disease/${user?.Patient_details?.UID}`);

        const { diseaseList } = res?.data;

        setDiseaseList(diseaseList);

      } catch (err) {
        console.error("error", err);
      }
    };

    if (user?.Patient_details?.UID) {
      DiseasesLists();
    }
  }, [user?.Patient_details?.UID]);


  let comparisonData = useMemo(() => {

    const isComparisonActive =
      selectedBeforeReports.length > 0 &&
      selectedAfterReports.length > 0;

    if (!isComparisonActive) return [];

    const beforeMap = new Map();
    const afterMap = new Map();

    selectedBeforeReports.forEach(reportId => {
      const reportGroup = Reports.find(rg => rg.id === reportId);
      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
      if (!json) return;

      [...(json.abnormal_parameters || []), ...(json.normal_parameters || [])]
        .forEach(param => {
          const key = normalizeName(param.name);
          if (!beforeMap.has(key)) {
            beforeMap.set(key, param);
          }
        });
    });

    selectedAfterReports.forEach(reportId => {
      const reportGroup = Reports.find(rg => rg.id === reportId);
      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
      if (!json) return;

      [...(json.abnormal_parameters || []), ...(json.normal_parameters || [])]
        .forEach(param => {
          const key = normalizeName(param.name);
          if (!afterMap.has(key)) {
            afterMap.set(key, param);
          }
        });
    });
    const commonKeys = [...beforeMap.keys()].filter(key =>
      afterMap.has(key)
    );

    return commonKeys.map(key => {
      const beforeP = beforeMap.get(key);
      const afterP = afterMap.get(key);

      return {
        name: beforeP?.name || afterP?.name,
        beforeValue: beforeP?.value ?? '-',
        afterValue: afterP?.value ?? '-',
        unit: beforeP?.unit || afterP?.unit || '',
        refRange:
          beforeP?.reference_range ||
          afterP?.reference_range ||
          '',
        beforeStatus: beforeP?.status,
        afterStatus: afterP?.status
      };
    });
  }, [Reports, selectedBeforeReports, selectedAfterReports]);


  return (
    <div className="Patient_Profile">
      {loading && <LoadingComponent loading={[teleConsultationModel, teleConsultationModel, shopModel]} />}
      <nav className="flex items-center justify-between p-2 mt-2 ml-2 mr-2 border rounded-lg">
        <Link to="/Appointments" className="flex items-center cursor-pointer">
          <IconButton>
            <ChevronLeft />
          </IconButton>
          <p className="ml-2 text-lg font-medium">Appointments</p>
        </Link>
        <Button
          onClick={() => {
            navigate('/AllChat', {
              state: {
                fromProfile: true,
                hideContacts: true,
                patientData: {
                  patient_id: user?.Patient_details?.UID,
                  patient_name: `${user?.Patient_details?.firstname} ${user?.Patient_details?.lastname}`,
                  avatar: user?.Patient_details?.src,
                  Appointment_id: user?.Appointment_details?.Appointment_id,
                  doctor_id: user?.Appointment_details?.doctor_id,
                  doctor_uid: user?.Appointment_details?.doctor_uid,
                  _id: user?.Patient_details?.UID
                }
              }
            });
          }}
          sx={{
            textTransform: 'none',
            backgroundColor: '#da6e0b',
            color: 'white',
            borderRadius: '5px',
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 500,
            padding: '8px 20px',
            '&:hover': {
              backgroundColor: '#c25e09',
            },
          }}
        >
          Chat
        </Button>
      </nav>

      <div className="Patient-appointment-details flex flex-row items-center mt-5">
        <div
          className="Patient-card flex flex-row items-center"
          style={{ flex: 1, width: "100%" }}
        >
          {user?.Patient_details?.src && user?.Patient_details?.src != "" ? (
            <div className="Patient-avatar">
              <img src={user?.Patient_details?.src}></img>
            </div>
          ) : (
            <div className="Patient-avatar">
              <p>
                <p>
                  {user?.Patient_details?.firstname.charAt(0)}{" "}
                  {user?.Patient_details?.lastname.charAt(0)}
                </p>
              </p>
            </div>
          )}

          <div>
            <p className="patient_name text-start">
              {user?.Patient_details?.firstname}{" "}
              {user?.Patient_details?.lastname}
            </p>
            <div className="patient-info-container">
              <div className="info-item">
                <img
                  src={
                    user?.Patient_details?.gender === "Male"
                      ? "/icons/Male_icon.png"
                      : "/icons/female_icon.png"
                  }
                  alt="Gender Icon"
                />
                <p>{user?.Patient_details?.gender}</p>
              </div>

              <div className="info-item">
                <img src="/icons/age.png" alt="Age Icon" />
                <p>
                  {user?.Patient_details?.dob
                    ? `${user.Patient_details.dob} years`
                    : "---"}
                </p>
              </div>

              <div className="info-item">
                <img src="/icons/weight.png" alt="Weight Icon" />
                <p>
                  {user?.Patient_details?.weight
                    ? user.Patient_details.weight
                    : "---"}
                </p>
              </div>

              <div className="info-item">
                <img src="/icons/height.png" alt="Height Icon" />
                <p>
                  {user?.Patient_details?.height
                    ? user.Patient_details.height
                    : "---"}
                </p>
              </div>

              <div className="info-item">
                <img src="/icons/mobile_icon.png" alt="Phone Icon" />
                <p>
                  {user?.Patient_details?.phone
                    ? user.Patient_details.phone
                    : "---"}
                </p>
              </div>

              <div className="info-item">
                <img src="/icons/mail_icon.png" alt="Email Icon" />
                <p>
                  {user?.Patient_details?.email
                    ? user.Patient_details.email
                    : "---"}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="your-div">
          <div
            className="flex flex-col w-full  appointment-id-section"
            style={{ flex: 1, width: "100%" }}
          >
            <div>
              <div>
                <img>
                </img>

              </div>
              <div className="flex flex-col justify-between ">
                <p style={{ fontFamily: "Libre Baskerville", fontWeight: "500", color: "#3B362A" }}>Personal Logging</p>
                <Button
                  onClick={() =>

                    navigate(`/Recommendations`, {
                      state: {
                        UID: user?.Patient_details?.UID,
                      },
                    })}

                  style={{
                    backgroundColor: "#da6e0b",
                    maxWidth: "100px",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    padding: "5px 10px",
                    cursor: "pointer",
                  }}>
                  View
                </Button>
              </div>
            </div>
            <div className="flex flex-row justify-evenly Patient-info-details">
              <div>
                <img src="/icons/date.png" alt="Date" />
                <p>
                  {user?.Appointment_details?.appointment_date
                    .split("-")
                    .reverse()
                    .join("-")}
                </p>
              </div>
              <div>
                <img src="/icons/clock.png" alt="Time" />
                <p>{user?.Appointment_details?.appointment_time}</p>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div
        className="Patient_Profile_list_container "
        style={{ alignItems: "flex-start", gap: "1rem" }}>
        <div className="Patient-more-info flex">
          <div className="flex pr-10">
            <div className="Patient-info ">

              <div className="mt-2 Patient-wellnessCategories pb-6">

                {user?.Wellness_Category?.Categories && (
                  <p>Wellness Categories</p>
                )}

                <div className="flex flex-row mt-2 items-center flex-wrap gap-5">
                  {user?.Wellness_Category?.Categories?.map((e, i) => {
                    if (e.selected) {
                      return (
                        <div key={i} className="text-center">
                          <img
                            src={e.imageUrl}
                            alt={e?.name}
                            className="mx-auto mb-2 wellness-img"
                          />
                          <p>{e?.name}</p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>

              {user?.ClinicalStudy && (
                <div className="Patient-appointment-details p-4 mb-3 border rounded-md shadow-sm">
                  <h1 className="disease-heading text-lg font-semibold mb-2">Study Details</h1>
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="font-medium">Group:</span>{" "}
                      <span className="text-gray-700">{user?.ClinicalStudy?.clinicalStudy}</span>
                    </p>
                    {user?.ClinicalStudy?.clinicalStudySub && (
                      <p>
                        <span className="font-medium">Sub-study Type:</span>{" "}
                        <span className="text-gray-700">{user.ClinicalStudy.clinicalStudySub}</span>
                      </p>
                    )}
                    {user?.ClinicalStudy?.StudyDuration && (
                      <p>
                        <span className="font-medium">Duration:</span>{" "}
                        <span className="text-gray-700">{user.ClinicalStudy.StudyDuration}</span>
                      </p>
                    )}
                    {user?.ClinicalStudy?.StudyStartDate && (
                      <p>
                        <span className="font-medium">Start Date:</span>{" "}
                        <span className="text-gray-700">{user.ClinicalStudy.StudyStartDate}</span>
                      </p>
                    )}
                    {user?.ClinicalStudy?.StudyendDate && (
                      <p>
                        <span className="font-medium">End Date:</span>{" "}
                        <span className="text-gray-700">{user.ClinicalStudy.StudyendDate}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {diseaseListNew && diseaseListNew?.length > 0 && (
                <div className="Patient-appointment-details p-4 mb-3">
                  <h1 className="disease-heading">Diseases</h1>
                  <ul className="disease-list">
                    {diseaseListNew?.map((disease, index) => (
                      <li key={disease.id || index}>{disease.symptoms}</li>
                    ))}
                  </ul>
                </div>
              )}

              {symptom && symptom.length > 0 && (
                <div className="Patient-appointment-details p-4 mb-3">
                  <h1 className="disease-heading">Symptoms</h1>
                  <ul className="disease-list">
                    {symptom?.map((disease, index) => (
                      <li key={index}>{disease.symptoms}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col gap-4">

                <div className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100">
                  <div
                    className="flex items-center gap-3  "
                    onClick={() => {
                      setSelectedPatientdetails(user?.Patient_details);
                      setSelectedAppointemntdetails(user?.Appointment_details);
                      resetPrescriptionState();
                      navigate(`/Prescriptions`);
                    }}
                  >
                    <img
                      src="/icons/My prescription.png"
                      alt="Prescription"
                      className="w-10 h-10"
                    />
                    <p className=" font-medium">Prescription</p>
                  </div>
                  <IconButton
                    onClick={() => {
                      setSelectedPatientdetails(user?.Patient_details);
                      setSelectedAppointemntdetails(user?.Appointment_details);
                      resetPrescriptionState();
                      navigate(`/Prescriptions`);
                    }}
                  >
                    <ChevronRight className="text-black" />
                  </IconButton>
                </div>

                <div
                  className="flex items-center justify-between bg-grey-300 p-4 rounded-lg w-full bg-stone-100 "
                  onClick={() => {
                    setSelectedPatientdetails(user?.Patient_details);
                    setSelectedAppointemntdetails(user?.Appointment_details);
                    navigate(
                      `/Reports/${user?.Appointment_details?.patient_id}`
                    );
                  }}
                >
                  <div className="flex items-center gap-3">
                    <img
                      src="/icons/My reports.png"
                      alt="Reports"
                      className="w-10 h-10"
                    />
                    <p className="text-black font-medium">Report</p>
                  </div>
                  <IconButton
                    component={Link}
                    to={`/Reports/${user?.Appointment_details?.patient_id}`}
                  >
                    <ChevronRight className="text-black" />
                  </IconButton>
                </div>


                <div className="flex gap-4 w-full">
                  <Button
                    style={{
                      textTransform: "none",
                      backgroundColor: "#DA6E0B",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 500,
                      padding: "10px 20px",
                      cursor: "pointer",
                    }}
                    component={Link}
                    to={`/Reschedule/${id}`}
                  >
                    Reschedule
                  </Button>

                  <Button

                    style={{
                      textTransform: "none",
                      backgroundColor: "#DA6E0B",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 500,
                      padding: "10px 20px",
                      cursor: "pointer",
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                    }}

                    component="a"
                    href={`/Call/${user?.Appointment_details?.patient_id}/${user?.Appointment_details?.consultation_type}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Consult
                  </Button>
                </div>
              </div>

            </div>
          </div>

          <div className="flex-1" style={{ boxSizing: "border-box" }}>
            <div className="flex gap-4">

              <div className="PrakritiChart flex-1">

                <div className="Prakriti-result-item">
                  <p>Prakriti Result</p>

                  <div className="Prakriti-r-c-item">
                    <p>Prakriti Assessment</p>
                    <div className="mt-2 ass-lables">
                      <div className="flex flex-row items-center justify-evenly">
                        <p>{user?.Prakriti_details?.result} {user?.Prakriti_details?.result && 'dominant'}</p>
                        <div>
                          <IconButton onClick={handleOpen4}>
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            onClick={() => {
                              setPrakritiCollection(
                                user?.Prakriti_details?.PrakritiCollection
                              );

                              handleOpen();
                            }}
                          >
                            <UpgradeIcon />
                          </IconButton>
                        </div>
                      </div>
                      {
                        user?.Prakriti_details &&
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-around",
                            alignItems: "center",
                            width: "100%",
                          }}
                          className="mt-2 ass-lables"
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#75623F",
                                marginRight: "8px",
                              }}
                            ></div>
                            <p className="pr-2">V :</p>
                            <p variant="body1">
                              {Math.round(user?.Prakriti_details?.vata)}%
                            </p>
                          </div>

                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#FF5C00",
                                marginRight: "8px",
                              }}
                            ></div>
                            <p className="pr-2">P :</p>
                            <p variant="body1">
                              {Math.round(user?.Prakriti_details?.pitta)}%
                            </p>
                          </div>

                          <div style={{ display: "flex", alignItems: "center" }}>
                            <div
                              style={{
                                width: "16px",
                                height: "16px",
                                backgroundColor: "#6CBA6C",
                                marginRight: "8px",
                              }}
                            ></div>
                            <p className="pr-2">K :</p>
                            <p variant="body1">
                              {Math.round(user?.Prakriti_details?.kapha)}%
                            </p>
                          </div>
                        </div>
                      }

                    </div>
                  </div>
                </div>
              </div>

              <div className="VikritiChart flex-1 flex flex-col">
                <div className="Vikriti-result-item flex-1">
                  <p>Vikriti</p>
                  <div className="mt-5 relative">
                    <DoshaBarChart
                      vikritiresultData={vikritiscore}
                      style={{ border: "1px solid red" }}
                    />
                    {isVikritiAILoading && !vikritiscore?.vata && !vikritiscore?.pitta && !vikritiscore?.kapha && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded">
                        <CircularProgress size="24px" sx={{ color: "#da6e0b" }} />
                      </div>
                    )}
                  </div>

                  <div className="Prakriti-r-c-item">
                    <p>Dosha Analysis</p>
                    <div className="mt-2 ass-lables">

                      <div className="flex items-center justify-between w-full">
                        {(diagnosisData?.increased_dosha?.dosha || user?.Vikriti_details?.Result) && (
                          <p className="text-sm">
                            {(diagnosisData
                              ? diagnosisData?.increased_dosha?.dosha?.toUpperCase()
                              : user?.Vikriti_details?.Result?.split('')[0]?.toUpperCase())}{" "}
                            dominant
                          </p>
                        )}

                        <IconButton onClick={handleOpen5}>
                          <UpgradeIcon />
                        </IconButton>
                      </div>

                      <div className="mt-2 text-sm">
                        <span className="font-semibold text-[#3B362A]">Reason : </span>
                        <span className="text-gray-600">
                          {user?.Vikriti_details?.reason && user?.Vikriti_details?.reason !== "None" ? (
                            user?.Vikriti_details?.reason
                          ) : vikritiAIReason ? (
                            vikritiAIReason
                          ) : diagnosisData?.increased_dosha?.reason && diagnosisData?.increased_dosha?.reason !== "None" ? (
                            diagnosisData?.increased_dosha?.reason
                          ) : isVikritiAILoading ? (
                            <span className="text-[#DA6E0B] font-medium">Generating...</span>
                          ) : (
                            "None"
                          )}
                        </span>
                      </div>

                      <div>
                        {/* <button onClick={handleOpenAi}>AI Suggestion</button> */}
                        {openAi && (
                          <div className="flex flex-col space-y-4">
                            {loading ? (
                              <div className="mt-5 flex-1 flex items-center justify-center">
                                <CircularProgress size="30px" sx={{ color: "#da6e0b" }} /></div>
                            ) : (
                              <>
                                <div className="flex items-start space-x-2 mt-4">
                                  <img
                                    src="/images/increaseDosha.png"
                                    className="w-14 h-14"
                                    alt="Increased Dosha"
                                  />
                                  <div>
                                    <p className="font-semibold text-sm">
                                      Increased Dosha:{" "}
                                      {diagnosisData?.increased_dosha?.dosha}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                      {diagnosisData?.increased_dosha?.reason}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start space-x-2">
                                  <img
                                    src="/images/decreaseDosha.png"
                                    className="w-14 h-14"
                                    alt="Decreased Dosha"
                                  />
                                  <div>
                                    <p className="font-semibold text-sm">
                                      Decreased Dosha:{" "}
                                      {diagnosisData?.decreased_dosha?.dosha}
                                    </p>
                                    <p className="text-sm text-gray-700">
                                      {diagnosisData?.decreased_dosha?.reason}
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      <div>
        {Reports?.some(reportItem =>
          reportItem?.Reports?.some(report => {
            const aiAnalysis = report?.ai_analysis;

            if (!aiAnalysis) return false;
            if (typeof aiAnalysis === 'string' && (aiAnalysis.includes('AI analysis failed') || aiAnalysis.trim() === '')) return false;

            try {
              const analysis = typeof aiAnalysis === 'object' ? aiAnalysis : JSON.parse(aiAnalysis);
              const abnormal = analysis?.abnormal_parameters || [];
              const normal = analysis?.normal_parameters || [];
              return abnormal.length > 0 || normal.length > 0;
            } catch {
              return false;
            }
          })
        ) && (
            <div className="Prakriti-result-item">
              <p>Report Summary</p>
              <div className="w-full mt-2">
                {(() => {
                  // Helper to check if a report has valid AI analysis
                  const hasValidAiAnalysis = (reportGroup) => {
                    const nestedReport = reportGroup?.Reports?.[0];
                    const aiAnalysis = nestedReport?.ai_analysis;
                    if (!aiAnalysis) return false;
                    if (typeof aiAnalysis === 'object') return true;
                    return typeof aiAnalysis === 'string' &&
                      !aiAnalysis.includes('AI analysis failed') &&
                      aiAnalysis.trim() !== '';
                  };

                  const allReportGroups = Reports ?? [];
                  const validReportGroups = allReportGroups.filter(hasValidAiAnalysis);

                  const isComparisonActive = selectedBeforeReports.length > 0 && selectedAfterReports.length > 0;



                  if (isComparisonActive) {
                    const cleanJson = (str) => {
                      try { return JSON.parse(str); } catch { return null; }
                    }

                    // Collect all parameters from all selected before reports
                    const allBeforeParams = [];

                    selectedBeforeReports.forEach(reportId => {
                      const reportGroup = Reports?.find(rg => rg.id === reportId);
                      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
                      if (json) {
                        allBeforeParams.push(...(json.abnormal_parameters || []), ...(json.normal_parameters || []));
                      }
                    });

                    // Collect all parameters from all selected after reports
                    const allAfterParams = [];

                    selectedAfterReports.forEach(reportId => {
                      const reportGroup = Reports?.find(rg => rg.id === reportId);
                      const json = cleanJson(reportGroup?.Reports?.[0]?.ai_analysis);
                      if (json) {
                        allAfterParams.push(...(json.abnormal_parameters || []), ...(json.normal_parameters || []));
                      }
                    });

                  }


                  const collected =
                    Reports?.flatMap(ri => ri?.Reports ?? [])?.flatMap((report, rIdx) => {
                      const aiAnalysis = report?.ai_analysis;
                      if (!aiAnalysis) return [];
                      if (typeof aiAnalysis === 'string' && (aiAnalysis.includes('AI analysis failed') || aiAnalysis.trim() === '')) return [];

                      let analysis;
                      try {
                        analysis = typeof aiAnalysis === 'object' ? aiAnalysis : JSON.parse(aiAnalysis);
                      } catch {
                        return [];
                      }

                      const abnormal = (analysis?.abnormal_parameters ?? []).map((p, i) => ({
                        ...p,
                        type: 'abnormal',
                        _k: `ab-${rIdx}-${i}-${p?.name ?? 'x'}`
                      }));

                      const normal = (analysis?.normal_parameters ?? []).map((p, i) => ({
                        ...p,
                        type: 'normal',
                        _k: `no-${rIdx}-${i}-${p?.name ?? 'x'}`
                      }));

                      return [...abnormal, ...normal];
                    }) ?? [];

                  const abnormalGroup = collected.filter(p => p.type === 'abnormal');
                  const normalGroup = collected.filter(p => p.type === 'normal');

                  return (
                    <Box sx={{ mb: 2 }}>
                      {validReportGroups.length > 0 && (
                        <>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 3,
                            p: 2.5,
                            background: 'linear-gradient(135deg, #fff8f0 0%, #ffe8d6 100%)',
                            borderRadius: 3,
                            border: '1px solid #DA6E0B',
                            boxShadow: '0 4px 12px rgba(218, 110, 11, 0.1)'
                          }}>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#DA6E0B', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>Before Treatment ({selectedBeforeReports.length})</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {selectedBeforeReports.length === 0 ? (
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: "'Poppins', sans-serif" }}>None Selected</Typography>
                                  ) : (
                                    selectedBeforeReports.map(id => (
                                      <Chip
                                        key={id}
                                        label={validReportGroups.find(rg => rg.id === id)?.ReportType}
                                        size="small"
                                        onDelete={() => setSelectedBeforeReports(prev => prev.filter(rid => rid !== id))}
                                        sx={{
                                          backgroundColor: '#4CAF50',
                                          color: 'white',
                                          fontWeight: 500,
                                          fontFamily: "'Poppins', sans-serif",
                                          '& .MuiChip-deleteIcon': { color: 'white' }
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#DA6E0B', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>After Treatment ({selectedAfterReports.length})</Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {selectedAfterReports.length === 0 ? (
                                    <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: "'Poppins', sans-serif" }}>None Selected</Typography>
                                  ) : (
                                    selectedAfterReports.map(id => (
                                      <Chip
                                        key={id}
                                        label={validReportGroups.find(rg => rg.id === id)?.ReportType}
                                        size="small"
                                        onDelete={() => setSelectedAfterReports(prev => prev.filter(rid => rid !== id))}
                                        sx={{
                                          backgroundColor: '#2196F3',
                                          color: 'white',
                                          fontWeight: 500,
                                          fontFamily: "'Poppins', sans-serif",
                                          '& .MuiChip-deleteIcon': { color: 'white' }
                                        }}
                                      />
                                    ))
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Button
                              variant="contained"
                              onClick={() => setOpenReportDialog(true)}
                              sx={{
                                textTransform: 'none',
                                backgroundColor: '#DA6E0B',
                                color: 'white',
                                fontWeight: 600,
                                fontFamily: "'Poppins', sans-serif",
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(218, 110, 11, 0.25)',
                                '&:hover': {
                                  backgroundColor: '#c46009',
                                  boxShadow: '0 6px 16px rgba(218, 110, 11, 0.35)'
                                }
                              }}
                            >
                              Select Reports
                            </Button>
                          </Box>

                          <Dialog
                            open={openReportDialog}

                            onClose={() => setOpenReportDialog(false)}
                            maxWidth="md"
                            fullWidth
                          >
                            <DialogTitle sx={{
                              fontWeight: 600,
                              fontFamily: "'Poppins', sans-serif",
                              background: 'linear-gradient(135deg, #fff8f0 0%, #ffe8d6 100%)',
                            }}>Select Reports for Comparison</DialogTitle>
                            <DialogContent>
                              <Grid container spacing={2} sx={{ mt: 1 }} alignItems="stretch">
                                {allReportGroups.map((reportGroup) => {
                                  const currentSelection = getReportSelection(reportGroup.id);
                                  const isSelected = currentSelection !== null;
                                  const canCompare = hasValidAiAnalysis(reportGroup);

                                  return (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={reportGroup.id}
                                      sx={{ display: 'flex' }}
                                    >
                                      <Card
                                        variant="outlined"
                                        sx={{
                                          height: '100%',
                                          width: '100%',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          borderColor: isSelected ? '#DA6E0B' : '#e0e0e0',
                                          backgroundColor: isSelected ? '#fff8f0' : 'white',
                                          opacity: canCompare ? 1 : 0.5,
                                          borderWidth: isSelected ? 2 : 1,
                                          boxShadow: isSelected
                                            ? '0 4px 16px rgba(218, 110, 11, 0.2)'
                                            : '0 2px 8px rgba(0,0,0,0.05)',
                                          transition: 'all 0.3s ease',
                                          '&:hover': {
                                            boxShadow: canCompare
                                              ? '0 6px 20px rgba(218, 110, 11, 0.15)'
                                              : 'none',
                                            transform: canCompare ? 'translateY(-2px)' : 'none'
                                          }
                                        }}
                                      >
                                        <CardContent sx={{ flexGrow: 1 }}>
                                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                            {reportGroup.ReportType}
                                          </Typography>

                                          <Typography variant="caption" color="textSecondary" display="block">
                                            Date: {new Date(reportGroup.CreatedAt).toLocaleDateString()}
                                          </Typography>

                                          <Typography variant="caption" color="textSecondary" display="block">
                                            Uploaded by: {reportGroup.ReportUploadedby || 'Unknown'}
                                          </Typography>

                                          {reportGroup.Comments && (
                                            <Typography
                                              variant="caption"
                                              color="textSecondary"
                                              display="block"
                                              sx={{ mt: 1, fontStyle: 'italic' }}
                                            >
                                              {reportGroup.Comments}
                                            </Typography>
                                          )}

                                          {!canCompare && (
                                            <Typography
                                              variant="caption"
                                              color="error"
                                              display="block"
                                              sx={{ mt: 1, fontWeight: 'bold' }}
                                            >
                                              No AI analysis available
                                            </Typography>
                                          )}
                                        </CardContent>

                                        <CardActions> <Stack direction="row" spacing={1}> <Chip label="Before" size="small" disabled={!canCompare || currentSelection === 'after'} onClick={() => { if (currentSelection === 'before') { setSelectedBeforeReports(prev => prev.filter(id => id !== reportGroup.id)); } else { setSelectedBeforeReports(prev => [...prev, reportGroup.id]); } }} sx={{ backgroundColor: currentSelection === 'before' ? '#4CAF50' : 'transparent', color: currentSelection === 'before' ? 'white' : '#4CAF50', borderColor: '#4CAF50', borderWidth: 1, borderStyle: 'solid', fontWeight: 600, fontFamily: "'Poppins', sans-serif", '&:hover': { backgroundColor: currentSelection === 'before' ? '#45a049' : 'rgba(76, 175, 80, 0.1)' } }} /> <Chip label="After" size="small" disabled={!canCompare || currentSelection === 'before'} onClick={() => { if (currentSelection === 'after') { setSelectedAfterReports(prev => prev.filter(id => id !== reportGroup.id)); } else { setSelectedAfterReports(prev => [...prev, reportGroup.id]); } }} sx={{ backgroundColor: currentSelection === 'after' ? '#2196F3' : 'transparent', color: currentSelection === 'after' ? 'white' : '#2196F3', borderColor: '#2196F3', borderWidth: 1, borderStyle: 'solid', fontWeight: 600, fontFamily: "'Poppins', sans-serif", '&:hover': { backgroundColor: currentSelection === 'after' ? '#1976D2' : 'rgba(33, 150, 243, 0.1)' } }} /> </Stack> </CardActions>
                                      </Card>
                                    </Grid>
                                  );
                                })}
                              </Grid>

                            </DialogContent>
                            <DialogActions>
                              <Button onClick={() => setOpenReportDialog(false)}>Close</Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      )}


                      {/* ── Comparison Table (shown below summary when active) ── */}
                      {isComparisonActive && comparisonData.length > 0 && (
                        <>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#3B362A', fontFamily: "'Poppins', sans-serif", fontSize: 14 }}>
                            Before vs After Comparison ({comparisonData.length} common parameters)
                          </Typography>
                          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 3 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={headerCellStyle}>Parameter</TableCell>
                                  <TableCell sx={headerCellStyle}>Before</TableCell>
                                  <TableCell sx={headerCellStyle}>After</TableCell>
                                  <TableCell sx={headerCellStyle}>Trend</TableCell>
                                  <TableCell sx={headerCellStyle}>Reference Range</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {comparisonData.map((row, idx) => {
                                  const unit = correctUnit(row.name, row.unit);

                                  const cleanNumeric = (val) => {
                                    if (val == null) return null;
                                    const match = String(val).replace(/,/g, '').match(/[-+]?\d*\.?\d+/);
                                    return match ? parseFloat(match[0]) : null;
                                  };

                                  const stripAllUnits = (val) => {
                                    if (val == null) return '-';
                                    const v = String(val).trim();
                                    const match = v.match(/^([-+]?\d*\.?\d+)/);
                                    return match ? match[1] : v;
                                  };

                                  const bNum = cleanNumeric(row.beforeValue);
                                  const aNum = cleanNumeric(row.afterValue);

                                  let trend = '→';
                                  let trendColor = '#888';
                                  let diffPct = null;

                                  if (bNum !== null && aNum !== null && bNum !== 0) {
                                    diffPct = ((aNum - bNum) / bNum) * 100;

                                    const bStat = (row.beforeStatus || '').toLowerCase();
                                    const aStat = (row.afterStatus || '').toLowerCase();
                                    const pName = (row.name || '').toLowerCase();

                                    // 1. Determine direction
                                    if (diffPct > 5) trend = '↑';
                                    else if (diffPct < -5) trend = '↓';

                                    // 2. Determine clinical "Goodness" for color
                                    if (trend !== '→') {
                                      // Improvement (Green)
                                      const isImprovement =
                                        (bStat !== 'normal' && aStat === 'normal') || // Became normal
                                        (aStat === 'high' && trend === '↓') ||        // High but decreasing
                                        (aStat === 'low' && trend === '↑') ||         // Low but increasing
                                        (aStat === 'normal' && bStat === 'normal' && (
                                          ((pName.includes('hemoglobin') || pName.includes('hgb') || pName.includes('rbc') || pName.includes('iron')) && trend === '↑') ||
                                          ((pName.includes('cholesterol') || pName.includes('glucose') || pName.includes('sugar') || pName.includes('alt') || pName.includes('ast')) && trend === '↓')
                                        ));

                                      // Worsening (Red)
                                      const isWorsening =
                                        (bStat === 'normal' && aStat !== 'normal') || // Became abnormal
                                        (aStat === 'high' && trend === '↑') ||        // High and increasing
                                        (aStat === 'low' && trend === '↓') ||         // Low and decreasing
                                        (aStat === 'normal' && bStat === 'normal' && (
                                          ((pName.includes('hemoglobin') || pName.includes('hgb') || pName.includes('rbc') || pName.includes('iron')) && trend === '↓') ||
                                          ((pName.includes('cholesterol') || pName.includes('glucose') || pName.includes('sugar') || pName.includes('alt') || pName.includes('ast')) && trend === '↑')
                                        ));

                                      if (isImprovement) trendColor = '#2e7d32'; // Green
                                      else if (isWorsening) trendColor = '#c62828'; // Red
                                      else {
                                        // Fallback to simple directional logic
                                        trendColor = trend === '↑' ? '#c62828' : '#2e7d32';
                                      }
                                    }
                                  }

                                  const beforeDisplay = stripAllUnits(row.beforeValue);
                                  const afterDisplay = stripAllUnits(row.afterValue);

                                  return (
                                    <TableRow key={idx} hover sx={{ backgroundColor: idx % 2 === 0 ? '#fafafa' : 'white', '&:hover': { backgroundColor: '#f1f5f9' } }}>
                                      <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>
                                        {row.name}{unit && <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 0.5 }}>({unit})</Typography>}
                                      </TableCell>
                                      <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <Typography sx={{ fontSize: 13 }}>{beforeDisplay}</Typography>
                                          {getStatusTag(row.beforeStatus)}
                                        </Stack>
                                      </TableCell>
                                      <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <Typography sx={{ fontSize: 13 }}>{afterDisplay}</Typography>
                                          {getStatusTag(row.afterStatus)}
                                        </Stack>
                                      </TableCell>
                                      <TableCell sx={{ textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                          <Typography sx={{ fontWeight: 700, fontSize: 18, color: trendColor, lineHeight: 1 }}>{trend}</Typography>
                                          {diffPct !== null && Math.abs(diffPct) > 0.1 && (
                                            <Typography sx={{ fontSize: 10, color: trendColor, fontWeight: 600 }}>
                                              {diffPct > 0 ? '+' : ''}{diffPct.toFixed(1)}%
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{row.refRange || '-'}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}

                      {isComparisonActive && comparisonData.length === 0 && (
                        <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed #ccc', borderRadius: 2, color: 'text.secondary' }}>
                          <Typography sx={{ fontFamily: "'Poppins', sans-serif" }}>No common parameters found between the selected Before and After reports.</Typography>
                        </Box>
                      )}

                      {/* ── Report Summary Table (always visible) ── */}
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, mb: 1.5, color: '#3B362A', fontFamily: "'Poppins', sans-serif", fontSize: 14 }}
                      >
                        All Parameters
                      </Typography>

                      <TableContainer
                        component={Paper}
                        elevation={2}
                        sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 3 }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={headerCellStyle}>Parameter</TableCell>
                              <TableCell sx={headerCellStyle}>Value</TableCell>
                              <TableCell sx={headerCellStyle}>Status</TableCell>
                              <TableCell sx={headerCellStyle}>Reference Range</TableCell>
                              <TableCell sx={headerCellStyle}>Note</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {abnormalGroup.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ backgroundColor: '#fff0f0', fontWeight: 700, color: '#c62828', fontSize: 12, py: 0.75, px: 2, fontFamily: "'Poppins', sans-serif", borderBottom: '1px solid #ffcdd2', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                  ⚠ Abnormal Parameters ({abnormalGroup.length})
                                </TableCell>
                              </TableRow>
                            )}
                            {abnormalGroup.map((param, j) => {
                              const unit = correctUnit(param.name, param.unit);
                              const valueStr = String(param.value ?? '');
                              const showUnit = unit && !valueStr.toLowerCase().includes(unit.toLowerCase());
                              return (
                                <TableRow key={param._k ?? `ab-${j}`} hover sx={{ backgroundColor: j % 2 === 0 ? '#fffafa' : '#fff5f5', '&:hover': { backgroundColor: '#fde8e8' } }}>
                                  <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: 13, color: '#3B362A' }}>{param.name ?? '-'}</TableCell>
                                  <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>
                                    {valueStr || '-'}
                                    {showUnit && <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 0.5 }}>{unit}</Typography>}
                                  </TableCell>
                                  <TableCell>{getStatusTag((param.status || '').toLowerCase())}</TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{param.reference_range || '-'}</TableCell>
                                  <TableCell sx={{ color: '#555', fontSize: 12 }}>{param.reason || '-'}</TableCell>
                                </TableRow>
                              );
                            })}
                            {normalGroup.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ backgroundColor: '#f0fff4', fontWeight: 700, color: '#2e7d32', fontSize: 12, py: 0.75, px: 2, fontFamily: "'Poppins', sans-serif", borderBottom: '1px solid #c8e6c9', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                  ✓ Normal Parameters ({normalGroup.length})
                                </TableCell>
                              </TableRow>
                            )}
                            {normalGroup.map((param, j) => {
                              const unit = correctUnit(param.name, param.unit);
                              const valueStr = String(param.value ?? '');
                              const showUnit = unit && !valueStr.toLowerCase().includes(unit.toLowerCase());
                              return (
                                <TableRow key={param._k ?? `no-${j}`} hover sx={{ backgroundColor: j % 2 === 0 ? '#fafffe' : '#f5fff8', '&:hover': { backgroundColor: '#e8f5e9' } }}>
                                  <TableCell sx={{ fontWeight: 600, fontFamily: "'Poppins', sans-serif", fontSize: 13, color: '#3B362A' }}>{param.name ?? '-'}</TableCell>
                                  <TableCell sx={{ fontFamily: "'Poppins', sans-serif", fontSize: 13 }}>
                                    {valueStr || '-'}
                                    {showUnit && <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 0.5 }}>{unit}</Typography>}
                                  </TableCell>
                                  <TableCell>{getStatusTag((param.status || '').toLowerCase())}</TableCell>
                                  <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{param.reference_range || '-'}</TableCell>
                                  <TableCell sx={{ color: '#555', fontSize: 12 }}>{param.reason || '-'}</TableCell>
                                </TableRow>
                              );
                            })}
                            {collected.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary', py: 4, fontFamily: "'Poppins', sans-serif" }}>No parameters found</TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>


                    </Box>
                  );
                })()}
              </div>
            </div>
          )}
      </div>

      <Dialog fullScreen open={open5} onClose={handleClose5} fullWidth>
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Vikriti Assessment Result
            </Typography>
            <IconButton onClick={handleClose5}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <div className="PrakritiAssesmentEdit">
          <TableContainer
            component={Paper}
            style={{ marginTop: "20px", borderRadius: "10px" }}
            className="AppointmentTable"
          >
            <Table>
              <TableHead>
                <TableRow className="tableheadtitle" align="center">
                  <TableCell>
                    <strong>Category</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Sub-Category</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Symptom</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Dosha Increase</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Dosha Decrease</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user?.Vikriti_details?.vikritiList?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        value={item.category}
                        onChange={(e) =>
                          handleEditChange(index, "category", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={item.sub_category}
                        onChange={(e) =>
                          handleEditChange(
                            index,
                            "sub_category",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 400 }}>
                      <Autocomplete
                        freeSolo
                        options={
                          symptomsList
                            ? symptomsList
                              .map((symptom) => symptom.toLowerCase())
                              .slice(0, 10000)
                            : []
                        }
                        value={item.symptoms}
                        onInputChange={(event, newSearchTerm) => {
                          setSearchTerm(newSearchTerm.toLowerCase());
                        }}
                        onChange={(event, newValue) => {

                          const selectedData = vikritidata.find(
                            (data) =>
                              data.symptoms.toLowerCase() ===
                              newValue?.toLowerCase()
                          );

                          if (selectedData) {
                            handleEditChange(
                              index,
                              "symptoms",
                              selectedData.symptoms
                            );

                            handleEditChange(
                              index,
                              "category",
                              selectedData.category
                            );

                            handleEditChange(
                              index,
                              "sub_category",
                              selectedData.sub_category
                            );

                            handleEditChange(
                              index,
                              "dosha_increase",
                              selectedData.dosha_increase
                            );

                            handleEditChange(
                              index,
                              "dosha_decrease",
                              selectedData.dosha_decrease
                            );

                            handleEditChange(
                              index,
                              "dhatu_increase",
                              selectedData.dhatu_increase
                            );

                            handleEditChange(
                              index,
                              "dhatu_decrease",
                              selectedData.dhatu_decrease
                            );

                            handleEditChange(
                              index,
                              "mala_increase",
                              selectedData.mala_increase
                            );

                            handleEditChange(
                              index,
                              "mala_decrease",
                              selectedData.mala_decrease
                            );

                          } else {
                            handleEditChange(index, "symptoms", newValue);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search Symptoms"
                            variant="outlined"
                          />
                        )}
                        filterOptions={(options, { inputValue }) => {
                          const lowerInput = inputValue.toLowerCase();
                          return symptomsList.filter(
                            (symptom) =>
                              symptom.toLowerCase().includes(lowerInput) // Match in lowercase
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        value={item.dosha_increase}
                        onChange={(e) =>
                          handleEditChange(
                            index,
                            "dosha_increase",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        value={item.dosha_decrease}
                        onChange={(e) =>
                          handleEditChange(
                            index,
                            "dosha_decrease",
                            e.target.value
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleRemove(index)}>
                        <HighlightOffIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Button
            variant="contained"
            onClick={handleAdd}
            style={{
              marginTop: "10px",
              marginRight: "10px",
              background: "#da6e0b",
            }}
          >
            Add Row
          </Button>
          <Button
            variant="contained"
            onClick={handleVikritiSavechanges}
            style={{ marginTop: "10px", background: "#da6e0b" }}
          >
            Save Changes
          </Button>
        </div>
      </Dialog>

      <Dialog fullScreen open={open} onClose={handleClose} fullWidth>
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Prakriti Assessment Result
            </Typography>

            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>

          </div>
        </DialogTitle>

        {/* <div className="PrakritiAssesmentEdit">
          {user?.Prakriti_details?.PrakritiCollection.map(
            (question, questionIndex) => {
              const selectedAnswerIndex = question.Answers.findIndex(
                (answer) => answer.isSelected
              );
              const selectedAnswer = question.Answers[selectedAnswerIndex];
              return (
                <div
                  className="PrakritiAssesmentEdit-container"
                  key={questionIndex}
                >
                  <div>
                    <p>{question.Question}</p>
                  </div>
                  <div>
                    {editQuestionIndex === questionIndex ? (
                      <FormControl fullWidth>
                        <Select
                          value={selectedAnswerIndex}
                          onChange={(e) =>
                            handleAnswerChange(
                              questionIndex,
                              parseInt(e.target.value, 10)
                            )
                          }
                        >
                          {question.Answers.map((answer, answerIndex) => (
                            <MenuItem key={answerIndex} value={answerIndex}>
                              {answer.Answer}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      selectedAnswer && (
                        <div className="answer-item selected">
                          <div className="answer-content">
                            <img
                              src={selectedAnswer.image}
                              alt={selectedAnswer.AnswerTag}
                              className="answer-image"
                            />
                            <p>{selectedAnswer.Answer}</p>
                          </div>
                          <div className="answer-actions">
                            <CheckCircle
                              style={{ marginRight: "10px", color: "#8CEB8C" }}
                            />
                            {selectedAnswer.vpk && (
                              <div className="vpk-indicator">
                                <span>{selectedAnswer.vpk.toUpperCase()}</span>
                                <ArrowUpward
                                  fontSize="small"
                                  style={{
                                    color: "#3b362a",
                                  }}
                                />
                              </div>
                            )}
                            <Edit
                              className="edit-answer-button"
                              onClick={() => handleEditAnswer(questionIndex)}
                              style={{
                                cursor: "pointer",
                                color: "#75623f",
                              }}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            }
          )}

          <div className="flex flex-row items-center justify-center m-3">
            <Button
              onClick={handleClick}
              sx={{
                textTransform: "none",
                borderRadius: "0",
                backgroundColor: "#DA6E0B",
                color: "white",
                border: "none",
                boxSizing: "border-box",
                borderRadius: "5px",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#DA6E0B",
                },
              }}
            >
              Save Changes
            </Button>
          </div>
        </div> */}

        <div className="PrakritiAssesmentEdit">
          {Array.isArray(user?.Prakriti_details?.PrakritiCollection) &&
            user.Prakriti_details.PrakritiCollection.map((question, questionIndex) => {
              const selectedAnswerIndex = question?.Answers?.findIndex(
                (answer) => answer?.isSelected
              );
              const selectedAnswer = question?.Answers[selectedAnswerIndex];
              return (
                <div
                  className="PrakritiAssesmentEdit-container"
                  key={questionIndex}
                >
                  <div>
                    <p>{question?.Question}</p>
                  </div>
                  <div>
                    {editQuestionIndex === questionIndex ? (
                      <FormControl fullWidth>
                        <Select
                          value={selectedAnswerIndex}
                          onChange={(e) =>
                            handleAnswerChange(
                              questionIndex,
                              parseInt(e.target.value, 10)
                            )
                          }
                        >
                          {question.Answers.map((answer, answerIndex) => (
                            <MenuItem key={answerIndex} value={answerIndex}>
                              {answer.Answer}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      selectedAnswer && (
                        <div className="answer-item selected">
                          <div className="answer-content">
                            <img
                              src={selectedAnswer.image}
                              alt={selectedAnswer.AnswerTag}
                              className="answer-image"
                            />
                            <p>{selectedAnswer.Answer}</p>
                          </div>
                          <div className="answer-actions">
                            <CheckCircle
                              style={{ marginRight: "10px", color: "#8CEB8C" }}
                            />
                            {selectedAnswer.vpk && (
                              <div className="vpk-indicator">
                                <span>{selectedAnswer.vpk.toUpperCase()}</span>
                                <ArrowUpward
                                  fontSize="small"
                                  style={{
                                    color: "#3b362a",
                                  }}
                                />
                              </div>
                            )}
                            <Edit
                              className="edit-answer-button"
                              onClick={() => handleEditAnswer(questionIndex)}
                              style={{
                                cursor: "pointer",
                                color: "#75623f",
                              }}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              );
            })}

          <div className="flex flex-row items-center justify-center m-3">
            <Button
              onClick={handleClick}
              sx={{
                textTransform: "none",
                borderRadius: "5px",
                backgroundColor: "#DA6E0B",
                color: "white",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                padding: "10px",
                "&:hover": {
                  backgroundColor: "#DA6E0B",
                },
              }}
            >
              Save Changes
            </Button>
          </div>

        </div>

      </Dialog>

      <Dialog open={open4} onClose={handleClose4} fullWidth maxWidth="sm">
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Prakriti Assessment Result
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0, mb: 0, fontSize: '10px', fontFamily: "'Poppins', sans-serif" }}>
              The values should sum to 100
            </Typography>
            <IconButton onClick={handleClose4}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <div style={{ padding: "16px", textAlign: "center" }}>
          <div>
            <TextField
              id="standard-search"
              label="Dominant"
              value={findVPKResult([
                user?.Prakriti_details?.vata,
                user?.Prakriti_details?.pitta,
                user?.Prakriti_details?.kapha,
              ])}
              type="search"
              variant="standard"
            />
          </div>
          <div className="pinput-c">
            {/* Vata */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#FF9800",
                    marginRight: "8px",
                  }}
                ></div>
                Vata
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={
                  user?.Prakriti_details?.vata === 0
                    ? "0"
                    : user?.Prakriti_details?.vata || ""
                }
                onChange={(e) => {
                  const input = e.target.value.trim();
                  const newValue = input === "" ? "" : parseInt(input, 10);

                  const currentPitta = parseInt(user?.Prakriti_details?.pitta || 0);
                  const currentKapha = parseInt(user?.Prakriti_details?.kapha || 0);
                  const total = (newValue || 0) + currentPitta + currentKapha;

                  if (total <= 100 || input === "") {
                    setuser((prevState) => ({
                      ...prevState,
                      Prakriti_details: {
                        ...prevState.Prakriti_details,
                        vata: newValue,
                      },
                    }));
                  } else {
                    toast.error("The sum of Prakriti values cannot exceed 100", {
                      position: "top-center",
                      autoClose: 2000,
                      theme: "dark",
                    });
                  }
                }}
              />
            </div>

            {/* Pitta */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#795548",
                    marginRight: "8px",
                  }}
                ></div>
                Pitta
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={
                  user?.Prakriti_details?.pitta === 0
                    ? "0"
                    : user?.Prakriti_details?.pitta || ""
                }
                onChange={(e) => {
                  const input = e.target.value.trim();
                  const newValue = input === "" ? "" : parseInt(input, 10);

                  const currentVata = parseInt(user?.Prakriti_details?.vata || 0);
                  const currentKapha = parseInt(user?.Prakriti_details?.kapha || 0);
                  const total = (newValue || 0) + currentVata + currentKapha;

                  if (total <= 100 || input === "") {
                    setuser((prevState) => ({
                      ...prevState,
                      Prakriti_details: {
                        ...prevState.Prakriti_details,
                        pitta: newValue,
                      },
                    }));
                  } else {
                    toast.error("The sum of Prakriti values cannot exceed 100", {
                      position: "top-center",
                      autoClose: 2000,
                      theme: "dark",
                    });
                  }
                }}
              />
            </div>

            {/* Kapha */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#8DE995",
                    marginRight: "8px",
                  }}
                ></div>
                Kapha
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={
                  user?.Prakriti_details?.kapha === 0
                    ? "0"
                    : user?.Prakriti_details?.kapha || ""
                }
                onChange={(e) => {
                  const input = e.target.value.trim();
                  const newValue = input === "" ? "" : parseInt(input, 10);

                  const currentVata = parseInt(user?.Prakriti_details?.vata || 0);
                  const currentPitta = parseInt(user?.Prakriti_details?.pitta || 0);
                  const total = (newValue || 0) + currentVata + currentPitta;

                  if (total <= 100 || input === "") {
                    setuser((prevState) => ({
                      ...prevState,
                      Prakriti_details: {
                        ...prevState.Prakriti_details,
                        kapha: newValue,
                      },
                    }));
                  } else {
                    toast.error("The sum of Prakriti values cannot exceed 100", {
                      position: "top-center",
                      autoClose: 2000,
                      theme: "dark",
                    });
                  }
                }}
              />
            </div>
          </div>


          <Button
            sx={{
              textTransform: "none",
              borderRadius: "0",
              backgroundColor: "#DA6E0B",
              color: "white",
              border: "none",
              boxSizing: "border-box",
              borderRadius: "5px",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: "10px",
              "&:hover": {
                backgroundColor: "#DA6E0B",
              },
            }}
            onClick={EditPrakritiResult}
          >
            Save
          </Button>
        </div>

      </Dialog>

      <Dialog open={open3} onClose={handleClose3} fullWidth maxWidth="sm">
        <DialogTitle>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Vikriti Assessment Result
            </Typography>
            <IconButton onClick={handleClose3}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>

        <div style={{ padding: "16px", textAlign: "center" }}>
          <div>
            <TextField
              id="standard-search"
              label="Dominant"
              value={user?.Vikriti_details?.Result}
              type="search"
              onChange={(e) => {
                const newValue = e.target.value;
                setuser((prevState) => ({
                  ...prevState,
                  Vikriti_details: {
                    ...prevState.Vikriti_details,
                    Result: newValue,
                  },
                }));
              }}
              variant="standard"
            />
          </div>

          <div className="pinput-c">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#FF9800",
                    marginRight: "8px",
                  }}
                ></div>
                Vata
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={user?.Vikriti_details?.vataAssScore}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setuser((prevState) => ({
                    ...prevState,
                    Vikriti_details: {
                      ...prevState?.Vikriti_details,
                      vataAssScore: parseInt(newValue),
                    },
                  }));
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#795548",
                    marginRight: "8px",
                  }}
                ></div>
                Pitta
              </div>
              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={user?.Vikriti_details?.pittaAssScore}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setuser((prevState) => ({
                    ...prevState,
                    Vikriti_details: {
                      ...prevState?.Vikriti_details,
                      pittaAssScore: parseInt(newValue),
                    },
                  }));
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    backgroundColor: "#8DE995",
                    marginRight: "8px",
                  }}
                ></div>
                Kapha
              </div>

              <input
                type="number"
                style={{
                  padding: "8px",
                  border: "1px solid #ccc",
                  width: "100px",
                  textAlign: "center",
                  background: "#F4F4EE",
                }}
                onKeyPress={handleKeyPress}
                value={user?.Vikriti_details?.kaphaAssScore}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setuser((prevState) => ({
                    ...prevState,
                    Vikriti_details: {
                      ...prevState?.Vikriti_details,
                      kaphaAssScore: parseInt(newValue),
                    },
                  }));
                }}
              />
            </div>
          </div>

          <Button
            sx={{
              textTransform: "none",
              borderRadius: "0",
              backgroundColor: "#DA6E0B",
              color: "white",
              border: "none",
              boxSizing: "border-box",
              borderRadius: "5px",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              padding: "10px",
              "&:hover": {
                backgroundColor: "#DA6E0B",
              },
            }}
            onClick={UpdateVkikriti}
          >
            Save
          </Button>
        </div>
      </Dialog>

    </div >

  );
}

export default Patient_Profile;
